/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, message, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors, hooks } from 'farmx-redux-core';
import SensorControl from 'components/settings/components/SensorControl';
import {
  useRenderReusableControls, generateSensorDepthOptions,
  preparethresholdFormObj, useThresholdHandlers, getThresholdConfig,
  prepareIrrigationThresholdFormObj, checkIsValidDate
} from 'helper/reusableControls';
import { getMainThresholdConfig } from 'helper/settingsHelper';
import { isMobile } from 'utils/detectDevice';
import { capabilities, FORM_RULES, GEC_RANCH_SWEETPOTATO_SOURCE } from '../../../constants';
import '../../settings/components/ruleSettingsPage.less';

interface ThresholdSettingsProps {
  selectedBlock: number | undefined;
  selectedRanch: number | undefined;
  showIrrigation?: boolean | undefined;
  showSprinkler?: boolean | undefined;
  showButton?: boolean | undefined;
  irrigationFormRef?: any;
  sprinklerFormRef?: any;
  isThreshold?: boolean;
  onSubmit?: any;
  ranchBlockConstraint?: boolean;
  controls?: any;
  prefix?: string;
  isTimeShiftIrrigation?: boolean;
  showMaxValveDurationControl?: boolean;
}

const minutesLabel = 'minutes';

// This will be changed in the future
const defaultDepthCount = 0;

const { selectSensorStatus, selectUserDepthFormat, selectUserPressureFormat } = selectors;

const { loadSensorStatus } = actions;

const { useUnits, useRanchBlockSelection } = hooks;

const ThresholdSettings: React.FC<ThresholdSettingsProps> = (props: any) => {
  const {
    selectedBlock, selectedRanch, showIrrigation, isThreshold,
    showSprinkler, showButton, irrigationFormRef, sprinklerformRef,
    onSubmit, ranchBlockConstraint, controls, prefix, isTimeShiftIrrigation,
    showMaxValveDurationControl
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getUserUnits = useUnits();
  const [irrigationForm] = Form.useForm();
  const [sprinklerCoolingform] = sprinklerformRef || Form.useForm();
  const form = irrigationFormRef || irrigationForm;
  const sprinklerform = sprinklerformRef || sprinklerCoolingform;

  const [initialValues, setInitialValues] = useState<any>({});
  const [selectedSensor, setSelectedSensor] = useState<any>(null);
  const [selectedSprinklerSensor, setSelectedSprinklerSensor] = useState<any>(null);
  const [irrigationSensor, setIrrigationSensor] = useState<any>(null);
  const [sprinklerSensor, setSprinklerSensor] = useState<any>(null);
  const [mainThresholds, setMainThresholds] = useState<any>([]);
  const [irrigationLoading, setIrrigationLoading] = useState(false);
  const [sprinklerLoading, setSprinklerLoading] = useState(false);
  const isSweetPotatoSourceSelected = GEC_RANCH_SWEETPOTATO_SOURCE === selectedRanch;
  const rules = FORM_RULES(t);
  const { requiredDepthField, requiredPositiveNumberField } = rules;

  const {
    [`${prefix}reference_sensor_type`]: type,
    [`${prefix}reference_sensor_identifier`]: identifier
  } = selectedSensor || {};

  const {
    [`${prefix}reference_sensor_type`]: sprinklerSensorType,
    [`${prefix}reference_sensor_identifier`]: sprinklerSensorIdentifier
  } = selectedSprinklerSensor || {};

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: t('${label} is required!'),
  };

  const {
    renderButton, renderInputFormItem,
    renderSelect,
  } = useRenderReusableControls(t);

  const depthFormat = useSelector(selectUserDepthFormat);
  const pressureFormat = useSelector(selectUserPressureFormat);
  const pressureLabel = pressureFormat === 'pounds_per_square_inch' ? 'psi' : 'mpa';

  // Get the sesor status and prepare sensor depth
  const sensorStatus = useSelector((state) => selectSensorStatus(state, type, identifier));
  const { depthCount, loading } = sensorStatus || {};
  const depthOptions = generateSensorDepthOptions(depthCount || defaultDepthCount,
    sensorStatus, getUserUnits, depthFormat);
  const sprinklerSensorStatus = useSelector((state) => selectSensorStatus(state,
    sprinklerSensorType, sprinklerSensorIdentifier));
  const { depthCount: sprinklerDepthCount, loading: sprinklerSensorLoading } = sprinklerSensorStatus || {};
  const sprinklerDepthOptions = generateSensorDepthOptions(sprinklerDepthCount || defaultDepthCount,
    sprinklerSensorStatus, getUserUnits, depthFormat);
  const { selectedObjFromState }: any = useRanchBlockSelection();
  const isRanch = selectedObjFromState?.type === 'ranch';
  const ranchId = isRanch ? Number(selectedObjFromState?.value) : selectedRanch;
  const { onFinishIrrigation, onFinishSprinker } = useThresholdHandlers(mainThresholds, ranchId, t);

  const { isEdit: isIrrigationEdit } = getThresholdConfig(mainThresholds, ranchId, 'irrigation');
  const { isEdit: isSprinklerEdit } = getThresholdConfig(mainThresholds, ranchId, 'sprinkler');

  const onFinish = (fieldValues: any) => {
    if (isThreshold) {
      if (!ranchId) message.error(t('Please select ranch'));
      else if (!isSweetPotatoSourceSelected) {
        onFinishIrrigation(fieldValues, isIrrigationEdit, setIrrigationLoading);
      }
    }
  };

  const onSprinkerFinish = (fieldValues: any) => {
    if (isThreshold) {
      if (!ranchId) message.error(t('Please select ranch'));
      else onFinishSprinker(fieldValues, isSprinklerEdit, setSprinklerLoading);
    }
  };

  useEffect(() => {
    if (!isThreshold) return;
    getMainThresholdConfig((success: boolean, response: any) => {
      if (success) {
        if (response?.data) {
          // Filter main thresholds by selected source ranch id
          const filteredThresholds = response.data
            ?.filter((obj: any) => obj?.ranch === ranchId);
          setMainThresholds(filteredThresholds);
          const formObj = preparethresholdFormObj(filteredThresholds, getUserUnits, pressureFormat);
          setSelectedSensor(formObj?.irrigation);
          setIrrigationSensor(formObj?.irrigationPressure);
          setSprinklerSensor(formObj?.sprinklerPressure);
          setSelectedSprinklerSensor(formObj?.sprinkler);
          setInitialValues(formObj);
        }
      } else {
        notification.error({
          message: t('Failed to fetch config'),
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranchId]);

  // Reset form fields on ranch change
  useEffect(() => {
    if (initialValues?.irrigation) {
      setInitialValues({ ...initialValues, depth: undefined, sprinklerDepth: undefined });
      setSelectedSensor(null);
      setIrrigationSensor(null);
      setSprinklerSensor(null);
      setSelectedSprinklerSensor(null);
      if (form) form.resetFields();
      if (sprinklerform) sprinklerform.resetFields();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, sprinklerform, ranchId]);

  // To autofill the field values that comes from props
  useEffect(() => {
    if (isThreshold || !controls) return;
    const formObj = prepareIrrigationThresholdFormObj(controls, prefix, getUserUnits, pressureFormat);
    setSelectedSensor(formObj?.irrigation);
    setIrrigationSensor(formObj?.irrigationPressure);
    form.setFieldsValue({ depth: (formObj?.depth)?.toString() });
    form.setFieldsValue({ irrigation: formObj?.irrigation });
    form.setFieldsValue({ irrigationPressure: formObj?.irrigationPressure });
    form.setFieldsValue({ irrigation_pressure_high: formObj?.irrigation_pressure_high });
    form.setFieldsValue({ irrigation_pressure_low: formObj?.irrigation_pressure_low });
    setInitialValues(formObj);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isThreshold) return;
    if (initialValues?.sprinklerPressure) {
      sprinklerform.setFieldsValue(initialValues);
      sprinklerform.setFieldsValue({
        sprinklerDepth: (initialValues?.sprinklerDepth)?.toString()
      });
    }
  }, [sprinklerform, initialValues]);

  useEffect(() => {
    if (!isThreshold) return;
    if (initialValues?.irrigation) {
      form.setFieldsValue(initialValues);
      if (!initialValues?.irrigation?.reference_sensor_type) {
        form.setFieldsValue({ irrigation: undefined });
      }
      if (!initialValues?.irrigationPressure?.reference_sensor_type) {
        form.setFieldsValue({ irrigationPressure: undefined });
      }
      form.setFieldsValue({ depth: (initialValues?.depth)?.toString()});
    }
  }, [form, initialValues]);

  // Load the sensor status
  useEffect(() => {
    if (!type || !identifier) return;
    dispatch(loadSensorStatus({ type, identifier }));
  }, [dispatch, identifier, type]);

  // Load the sprinkler sensor status
  useEffect(() => {
    if (!sprinklerSensorType || !sprinklerSensorIdentifier) return;
    dispatch(loadSensorStatus({ type: sprinklerSensorType,
      identifier: sprinklerSensorIdentifier }));
  }, [dispatch, sprinklerSensorIdentifier, sprinklerSensorType]);
  
  function customOnFinish(d: any) {
    if (onSubmit) {
      if (isTimeShiftIrrigation) {
        const {
          [`${prefix}starting_time_window`]: sourceStartingTimeWindow,
          [`${prefix}ending_time_window`]: sourceEndingTimeWindow,
          starting_time_window, ending_time_window,
        } = d || {};

        const { isValid, message } = checkIsValidDate(starting_time_window, ending_time_window,
          sourceStartingTimeWindow, sourceEndingTimeWindow, t)

        if (isValid) onSubmit(d);
        else if (!isValid) { // Show message and prevent form submitting
          notification.error({ message })
          return;
        }
      } else onSubmit(d);
    }
    else onFinish(d)
  }

  function renderMaxValveOpeningDurationControl(formAttribute: string) {
    return renderInputFormItem(formAttribute, t('Max Valve Opening Period'),
      minutesLabel, null, requiredPositiveNumberField, 'Minutes')
  }

  return (
    <div className={`rule-setting-form ${isMobile ? 'width-100-percent' : ''}
     ${isTimeShiftIrrigation ? 'width-auto' : ''}`}>
      {showIrrigation && !isSweetPotatoSourceSelected &&   (
        <Form
          form={form}
          layout="vertical"
          name="Threshold Settings"
          onFinish={customOnFinish}
          validateMessages={validateMessages}
          initialValues={initialValues}
        >
          <div className="flex-space-between">
            <SensorControl
              form={form}
              selectedBlock={ranchBlockConstraint ? selectedBlock : undefined}
              selectedRanch={ranchBlockConstraint ? ranchId : undefined}
              setSelectedSensor={setSelectedSensor}
              sensor={selectedSensor}
              formLabel={`${t('Referenced SMP for Irrigation')} ${isTimeShiftIrrigation ? t('at source') : ''}`}
              formName="irrigation"
              prefix={prefix}
              fullWidth={isMobile ? false : true}
            />
            <Form.Item
              className="font-bold width-25 margin-left-5"
              label={t('Depth')}
              name="depth"
              rules={requiredDepthField}
            >
              {renderSelect(depthOptions, 'Select Depth', initialValues?.depth,
                undefined, undefined, loading)}
            </Form.Item>
          </div>
          <SensorControl
            form={form}
            selectedBlock={ranchBlockConstraint ? selectedBlock : undefined}
            selectedRanch={ranchBlockConstraint ? ranchId : undefined}
            setSelectedSensor={setIrrigationSensor}
            sensor={irrigationSensor}
            formLabel={`${t('Referenced Pressure Sensor for Irrigation')} ${isTimeShiftIrrigation ? t('at source') : ''}`}
            formName="irrigationPressure"
            sensorTypes={capabilities?.water_pressure}
            prefix={prefix?.length ? `${prefix}pressure_` : ''}
          />
          {renderInputFormItem('irrigation_pressure_high', t('Pressure High Threshold'),
            pressureLabel, null, requiredPositiveNumberField, 'High')}
          {renderInputFormItem('irrigation_pressure_low', t('Pressure Low Threshold'),
            pressureLabel, null, requiredPositiveNumberField, 'Low')}
          {showMaxValveDurationControl
            && renderMaxValveOpeningDurationControl('irrigation_max_valve_opening_duration')}

          {showButton && (
            <Form.Item className="font-bold flex-row-center justify-center">
              {renderButton(isIrrigationEdit ? 'Update' : 'Save',
                'primary', 'submit', undefined, undefined, true, irrigationLoading)}
            </Form.Item>
          )}
        </Form>
      )}

      {(showSprinkler || isSweetPotatoSourceSelected) &&  (
        <Form
          form={sprinklerform}
          layout="vertical"
          name="Sprinkler Threshold Settings"
          onFinish={(d) => {
            if (onSubmit) onSubmit(d);
            else onSprinkerFinish(d)
          }}
          validateMessages={validateMessages}
          initialValues={initialValues}
        >
          <div className="flex-space-between">
            <SensorControl
              form={sprinklerform}
              selectedBlock={ranchBlockConstraint ? selectedBlock : undefined}
              selectedRanch={ranchBlockConstraint ? selectedRanch : undefined}
              setSelectedSensor={setSelectedSprinklerSensor}
              sensor={selectedSprinklerSensor}
              formLabel={`${t('Referenced SMP for Sprinkler')} ${isTimeShiftIrrigation ? t('at source') : ''}`}
              formName="sprinkler"
              prefix={prefix}
              fullWidth={isMobile ? false : true}
            />
            <Form.Item
              className="font-bold width-25 margin-left-5"
              label={t('Depth')}
              name="sprinklerDepth"
              rules={requiredDepthField}
            >
              {renderSelect(sprinklerDepthOptions, 'Select Depth', initialValues?.sprinklerDepth,
                undefined, undefined, sprinklerSensorLoading)}
            </Form.Item>
          </div>
          <SensorControl
            form={sprinklerform}
            selectedBlock={ranchBlockConstraint ? selectedBlock : undefined}
            selectedRanch={ranchBlockConstraint ? selectedRanch : undefined}
            setSelectedSensor={setSprinklerSensor}
            sensor={sprinklerSensor}
            formLabel="Referenced Pressure Sensor for Sprinkler"
            formName="sprinklerPressure"
            sensorTypes={capabilities?.water_pressure}
          />
          {renderInputFormItem('sprinkler_pressure_high', t('Pressure High Threshold'),
            pressureLabel, null, requiredPositiveNumberField, 'High')}
          {renderInputFormItem('sprinkler_pressure_low', t('Pressure Low Threshold'),
            pressureLabel, null, requiredPositiveNumberField, 'Low')}
          {showMaxValveDurationControl
            && renderMaxValveOpeningDurationControl('sprinkler_max_valve_opening_duration')}

          {showButton && (
            <Form.Item className="font-bold flex-row-center justify-center">
              {renderButton(isSprinklerEdit ? 'Update' : 'Save',
                'primary', 'submit', undefined, undefined, true, sprinklerLoading)}
            </Form.Item>
          )}
        </Form>
      )}
    </div>
  );
};

ThresholdSettings.defaultProps = {
  selectedBlock: 0,
  showIrrigation: true,
  showSprinkler: true,
  showButton: true,
  isThreshold: true,
  ranchBlockConstraint: true,
  prefix: '',
  showMaxValveDurationControl: true,
};

export default ThresholdSettings;
